import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {RefModelStoreService, TakeUntilComponentDestroy} from '@q9elements/ui-kit/common';
import {OptimizeAssignee} from '@q9elements/ui-kit/refmodels';
import {Observable, of} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import {RefModelService} from '../../../../../../../../../services/ref-model.service';

import {isValidNodeAssignee} from '../../../../validators/node-assignee.valiator';

const UNASSIGNED = 'Unassigned';

@Component({
  selector: 'app-user-search',
  templateUrl: './user-search.component.html',
  styleUrls: ['./user-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSearchComponent extends TakeUntilComponentDestroy implements OnInit {
  @Input() public value: string;
  @Output() public userSelected = new EventEmitter<string>();

  public assignees$: Observable<OptimizeAssignee[]>;
  public assigneeFormControl: UntypedFormControl;
  public error: string;
  public matError: any;
  public noAssignees: boolean;
  public showUnassigned = false;
  public hasAssignees: boolean;

  private get refModel() {
    return this.rmeStore.refModel;
  }

  constructor(
    private rmeStore: RefModelStoreService,
    private refModelService: RefModelService
  ) {
    super();
  }

  ngOnInit() {
    this.assigneeFormControl = new UntypedFormControl('', isValidNodeAssignee());
    this.setFormAssignees();
    this.setupAssigneesObserver();
  }

  private setFormAssignees() {
    this.hasAssignees = Boolean(this.value?.toLowerCase() !== UNASSIGNED);

    if (Boolean(this.value)) {
      this.assigneeFormControl.patchValue(this.value, {emitEvent: true});
      this.assigneeFormControl.markAsDirty();
    }
  }

  private setupAssigneesObserver(): void {
    this.assignees$ = this.assigneeFormControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      filter((userInput: any) => typeof userInput === 'string'),
      switchMap((userInput: string) => {
        userInput = userInput || this.assigneeFormControl.value;

        this.userSelected.emit(userInput);

        if (!userInput || userInput.length < 2) {
          this.matError = 'POPUPS.USER_NOT_SPECIFIED';
          return of([]);
        }

        return this.getOptimizeAssignees(userInput);
      })
    );
  }

  private getOptimizeAssignees(pattern: string): Observable<OptimizeAssignee[]> {
    return this.refModelService.getOptimizeAssignees(this.refModel._id, pattern).pipe(
      tap(response => {
        this.showUnassigned = RegExp(pattern, 'i').test(UNASSIGNED);

        return response;
      }),
      tap(response => {
        this.noAssignees = !response.length;

        if (this.noAssignees && !this.showUnassigned) {
          this.matError = 'POPUPS.USER_NOT_FOUND';
        }
      })
    );
  }

  // used in [errorStateMatcher] input
  isErrorState(control: UntypedFormControl | null): boolean {
    if (control.hasError('nodeAssignee')) {
      this.matError = 'RUBIC_CUBE.VALUE_SHOULD_BE_EMAIL';
    } else {
      if (this.matError === 'RUBIC_CUBE.VALUE_SHOULD_BE_EMAIL') {
        this.matError = '';
      }
    }

    return !!(control && control.dirty && (control.invalid || this.matError));
  }

  onSelectAssigneeHandler(event: any) {
    this.matError = '';

    this.userSelected.emit(event.option.value.email || event.option.value);
  }

  public displayAssignee(assignee: OptimizeAssignee): string {
    if (String(assignee) === UNASSIGNED) {
      return UNASSIGNED;
    } else {
      return assignee && `${assignee.email || assignee}`;
    }
  }
}
